import { Extension } from '@tiptap/core';
import { lift, setBlockType } from '@tiptap/pm/commands';
import type { Node } from '@tiptap/pm/model';
import { type EditorState, Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { slashQuery } from './block-commands';
import { moveBlock } from './move-block';

type Dispatch = ((tr: Transaction) => void) | undefined;

/**
 * Backspace at the start of a block, the Notion way.
 *
 * ProseMirror's own Backspace joins a heading straight into the block above
 * it. Notion first turns the heading back into text, and steps the first
 * paragraph of a quote out in front of the quote, so that one press undoes
 * the block's shape and the next one joins. The phone follows the same rule in
 * `DocumentEditor.joinBackward`.
 */
export function backspaceAtBlockStart(state: EditorState, dispatch: Dispatch): boolean {
  const { $from, empty } = state.selection;

  if (!empty || $from.parentOffset !== 0 || !$from.parent.isTextblock) {
    return false;
  }

  if ($from.parent.type.name === 'heading') {
    return setBlockType(state.schema.nodes.paragraph)(state, dispatch);
  }

  if (
    $from.parent.type.name === 'paragraph' &&
    $from.depth > 1 &&
    $from.node(-1).type.name === 'blockquote' &&
    $from.index(-1) === 0
  ) {
    return lift(state, dispatch);
  }

  return false;
}

/** Moves the top-level block holding the selection. */
export function moveSelectedBlock(
  state: EditorState,
  dispatch: Dispatch,
  direction: -1 | 1,
): boolean {
  const tr = moveBlock(state, state.selection.$from.index(0), direction);

  if (!tr) {
    return false;
  }

  dispatch?.(tr);

  return true;
}

/** Whether the caret sits at the very start of the document. */
export function atDocumentStart(state: EditorState): boolean {
  const { $from, empty } = state.selection;

  return empty && $from.pos === 1 && $from.depth === 1 && $from.parentOffset === 0;
}

/**
 * The slash command being typed, as document positions: the slash's position
 * and the caret's.
 */
export function slashRange(state: EditorState): { from: number; to: number; query: string } | null {
  const { $from, empty } = state.selection;

  if (!empty || !['paragraph', 'heading'].includes($from.parent.type.name)) {
    return null;
  }

  const found = slashQuery($from.parent.textBetween(0, $from.parentOffset, undefined, '￼'));

  if (!found) {
    return null;
  }

  return { from: $from.start() + found.start, to: $from.pos, query: found.query };
}

/**
 * The words an empty block shows, as Notion shows them.
 *
 * Only the block with the caret asks for commands, so an empty line in the
 * middle of a note does not shout "Type '/'"; a heading, list item, task or
 * quote says what it is whenever it is empty.
 */
export function blockPlaceholder(options: {
  node: Node;
  parent: Node | null;
  hasAnchor: boolean;
  isEmptyDoc: boolean;
}): string {
  const { node, parent, hasAnchor, isEmptyDoc } = options;

  if (node.type.name === 'heading') {
    return `Heading ${node.attrs.level}`;
  }

  if (node.type.name !== 'paragraph') {
    return '';
  }

  switch (parent?.type.name) {
    case 'listItem':
      return 'List';
    case 'taskItem':
      return 'To-do';
    case 'blockquote':
      return 'Empty quote';
    default:
      break;
  }

  if (isEmptyDoc) {
    return "Write something, or type '/' for commands…";
  }

  return hasAnchor ? "Type '/' for commands" : '';
}

/**
 * The placeholder decorations for a document: one per empty text block that
 * has something to say.
 *
 * Built from `descendants`, which hands over each node's parent, rather than
 * from Tiptap's Placeholder extension, whose callback gives only a position.
 * Resolving that position against `editor.state` runs one transaction behind
 * during an update, and when an input rule had just wrapped a paragraph into a
 * list the stale document threw, which undid the wrap. This never resolves.
 */
export function placeholderDecorations(state: EditorState): DecorationSet {
  const { doc, selection } = state;
  const anchor = selection.$anchor.pos;

  const isEmptyDoc =
    doc.childCount === 1 &&
    doc.firstChild?.type.name === 'paragraph' &&
    doc.firstChild.content.size === 0;

  const decorations: Decoration[] = [];

  doc.descendants((node, pos, parent) => {
    if (!node.isTextblock) {
      return true;
    }

    if (node.content.size > 0) {
      return false;
    }

    const text = blockPlaceholder({
      node,
      parent,
      hasAnchor: anchor >= pos && anchor <= pos + node.nodeSize,
      isEmptyDoc,
    });

    if (text) {
      decorations.push(
        Decoration.node(pos, pos + node.nodeSize, {
          class: 'is-empty',
          'data-placeholder': text,
        }),
      );
    }

    return false;
  });

  return DecorationSet.create(doc, decorations);
}

export const BlockPlaceholders = Extension.create({
  name: 'blockPlaceholders',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('blockPlaceholders'),
        props: {
          decorations: state => (this.editor.isEditable ? placeholderDecorations(state) : null),
        },
      }),
    ];
  },
});

/**
 * Notion's keys that Tiptap does not already bind: Backspace shaping, block
 * moves on Mod+Shift+Arrow beside the older Alt+Shift pair, and the
 * Mod+Option+number block shortcuts Notion documents.
 */
export const NotionKeys = Extension.create({
  name: 'notionKeys',

  addKeyboardShortcuts() {
    const move = (direction: -1 | 1) => () =>
      moveSelectedBlock(this.editor.state, tr => this.editor.view.dispatch(tr), direction);

    return {
      Backspace: () =>
        backspaceAtBlockStart(this.editor.state, tr => this.editor.view.dispatch(tr)),
      'Mod-Shift-ArrowUp': move(-1),
      'Mod-Shift-ArrowDown': move(1),
      'Alt-Shift-ArrowUp': move(-1),
      'Alt-Shift-ArrowDown': move(1),
      'Mod-Alt-4': () => this.editor.commands.toggleTaskList(),
      'Mod-Alt-5': () => this.editor.commands.toggleBulletList(),
      'Mod-Alt-6': () => this.editor.commands.toggleOrderedList(),
      'Mod-Alt-8': () => this.editor.commands.toggleCodeBlock(),
    };
  },
});
